/* ─────────────────────────────────────────────────────────────
   Identity error copy — FR-F1. The one map every later identity task
   (FR-F2 to FR-F11) reads for a BE identity-domain refusal.
   ───────────────────────────────────────────────────────────── */

import { gqlErrorCode, gqlErrorData } from '@/lib/gql_error'
import type { IdentityBlockedStatus } from '@/components/shared/IdentityBlockedBanner'

/**
 * Keyed by the symbolic `extensions.errorType`. `gqlErrorCode` reads `errorType` first,
 * falling back to the numeric `extensions.code`. Codes not listed here fall through to the
 * caller's own fallback message (`getIdentityErrorMessage` returns `undefined`).
 *
 * BE R4 is not live yet (T03 is the codegen boundary), so the exact wire `errorType` string for
 * each new code cannot be confirmed from a real response. The checked-out BE enum
 * (`entity-backend/apps/entity-management-svc/src/common/errors/error-type.enum.ts`) already
 * ships the natural-person family UNPREFIXED (`NATURAL_PERSON_NOT_FOUND = 'NATURAL_PERSON_NOT_FOUND'`,
 * etc.) while most other domains are `EM_`-prefixed (`APPOINTMENT_ALREADY_ENDED = 'EM_APPOINTMENT_ALREADY_ENDED'`),
 * and the PRD's own FR-9 quote names this error's `ErrorType` member `NATURAL_PERSON_EMAIL_REQUIRED`
 * (unprefixed) against FR-F1's `EM_`-prefixed FE-facing name — so both codes below are mapped under
 * both forms, whichever the BE actually sends still resolves. (`src/lib/acl/em-errors.ts`'s
 * `EM_10236` / `APPOINTMENT_ALREADY_ENDED` dual-key is a related but different hedge — code vs.
 * `errorType` field, not prefixed vs. unprefixed name — so it is not cited as the same ambiguity.)
 *
 * `retryable` is read by `getIdentityRetryable`, the one place that decides whether a refusal
 * gets a Retry action — a later identity task reads it from here rather than re-deciding per
 * call site (FE-C9, recorded in T02's TASK-AMEND).
 */
export const IDENTITY_ERROR_COPY: Record<string, { message: string; retryable?: boolean }> = {
  EM_NATURAL_PERSON_EMAIL_REQUIRED: { message: 'Enter an email address for this person' },
  NATURAL_PERSON_EMAIL_REQUIRED: { message: 'Enter an email address for this person' },
  EM_IDENTITY_SERVICE_UNAVAILABLE: { message: 'Could not save just now. Try again.', retryable: true },
  IDENTITY_SERVICE_UNAVAILABLE: { message: 'Could not save just now. Try again.', retryable: true },
  EM_OFFICER_EMAIL_REQUIRED: { message: 'Enter an email address for each new officer' },
  OFFICER_EMAIL_REQUIRED: { message: 'Enter an email address for each new officer' },
  EM_NATURAL_PERSON_IDENTITY_BLOCKED: { message: 'This person cannot be used until their identity is resolved' },
  NATURAL_PERSON_IDENTITY_BLOCKED: { message: 'This person cannot be used until their identity is resolved' },
}

/** `gqlErrorCode(err)` then a lookup — `undefined` when the code is absent or unmapped. */
export function getIdentityErrorMessage(err: unknown): string | undefined {
  const code = gqlErrorCode(err)
  return (code && IDENTITY_ERROR_COPY[code]?.message) ?? undefined
}

/** Whether a mapped identity refusal should offer a Retry action — `false` for an unmapped code. */
export function getIdentityRetryable(err: unknown): boolean {
  const code = gqlErrorCode(err)
  return Boolean(code && IDENTITY_ERROR_COPY[code]?.retryable)
}

const BLOCKED_CODES = new Set(['EM_NATURAL_PERSON_IDENTITY_BLOCKED', 'NATURAL_PERSON_IDENTITY_BLOCKED'])

/**
 * Reads an `EM_NATURAL_PERSON_IDENTITY_BLOCKED` refusal's `{ partyId, identityStatus }` payload
 * (`identity-usable.ts:48` on the BE) so a call site can render `IdentityBlockedBanner` +
 * `BlockedPersonLink` without re-deriving the shape itself. `undefined` for any other error.
 */
export function getBlockedPersonIdentity(
  err: unknown,
): { partyId: string; status: IdentityBlockedStatus } | undefined {
  const code = gqlErrorCode(err)
  if (!code || !BLOCKED_CODES.has(code)) return undefined
  const data = gqlErrorData(err)
  const partyId = data?.partyId
  const status = data?.identityStatus
  if (typeof partyId !== 'string' || typeof status !== 'string') return undefined
  return { partyId, status: status as IdentityBlockedStatus }
}
